import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { PlainDate } from 'temporal-luxon';
import { Button, Card, DatePicker, Icon, Input, Select, Text, useLocalizeMessage } from 'libs.nucleus';
import { getOperatorConfig, getOperatorsForFieldType, normalizeOperator, operatorRequiresValue, toCanonicalFieldType, } from '../../../utils/field_type_operators.utils';
import { getSelectedFieldsForSettings, PALETTE_COLORS, sanitizeConditionalFormatsForPalette } from '../../utils';
/** Render a small color swatch next to the label */
const ColorSwatch = ({ hex }) => (_jsx("span", { className: 'inline-block w-3 h-3 rounded-full border border-neutral-300 mr-2 flex-shrink-0', style: { backgroundColor: hex } }));
const toPlainDateOrUndefined = (value) => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    try {
        const iso = String(value).split('T')[0];
        return PlainDate.from(iso);
    }
    catch {
        return undefined;
    }
};
const resolveFieldType = (columnKey, columns) => {
    const column = columns.find((c) => c.key === columnKey);
    return toCanonicalFieldType(column?.type);
};
/**
 * Derive the effective field type + canonical operator for a rule condition.
 * Priority: the persisted `fieldType` on the condition, then the current column
 * type from the chart config (so operators stay consistent as columns change),
 * then a `string` fallback.
 */
const resolveEffectiveOperator = (condition, columns) => {
    const fieldType = condition.fieldType
        ? toCanonicalFieldType(condition.fieldType)
        : resolveFieldType(condition.columnName, columns);
    const operator = normalizeOperator(condition.operator, fieldType);
    return { fieldType, operator };
};
const pickDefaultOperator = (fieldType) => getOperatorsForFieldType(fieldType)[0]?.value ?? 'equals';
export const ColorsSection = ({ state, onChange, isEditable }) => {
    const translate = useLocalizeMessage();
    const isNumberChart = state.chartType === 'number';
    const isPieChart = state.chartType === 'pie';
    // Number charts support conditional formatting (same as bar/line/table); pie charts do not.
    const hideConditionalFormatting = isPieChart;
    const currentPalette = state.palette || 'categorical';
    const selectedFields = useMemo(() => getSelectedFieldsForSettings(state), [state]);
    // Column options for the "If" column picker — derived from selected columns
    const columnOptions = useMemo(() => selectedFields.map((field) => ({ label: field.name, value: field.key })), [selectedFields]);
    const paletteOptions = useMemo(() => [
        { label: translate('Neutral'), value: 'neutral' },
        { label: translate('Categorical'), value: 'categorical' },
        { label: translate('Purple'), value: 'purple' },
        { label: translate('Greyscale'), value: 'greyscale' },
    ], [translate]);
    // Color options for conditional formatting — restricted to the selected palette.
    // Number charts render Neutral as dark grey (#3A3B3D); other chart types keep light Neutral.
    const colorOptions = useMemo(() => {
        const paletteColors = isNumberChart && currentPalette === 'neutral'
            ? [{ label: 'Neutral', value: 'neutral', hex: '#3A3B3D' }]
            : PALETTE_COLORS[currentPalette];
        return paletteColors.map((c) => ({
            label: c.label,
            value: c.value,
            startAdornment: _jsx(ColorSwatch, { hex: c.hex }),
        }));
    }, [currentPalette, isNumberChart]);
    const handlePaletteChange = useCallback((option) => {
        const newPalette = option.value;
        onChange({
            ...state,
            palette: newPalette,
            conditionalFormats: sanitizeConditionalFormatsForPalette(state.conditionalFormats, newPalette),
        });
    }, [state, onChange]);
    // Build a flat list of conditions from all conditional format rules, migrating
    // legacy operators so the UI always shows canonical values.
    const conditions = useMemo(() => {
        return (state.conditionalFormats || []).flatMap((rule) => rule.conditions.map((cond) => {
            const { fieldType, operator } = resolveEffectiveOperator({
                fieldType: cond.fieldType,
                operator: String(cond.operator),
                columnName: rule.columnName,
            }, selectedFields);
            return {
                columnName: rule.columnName,
                operator,
                value: cond.value,
                endValue: cond.endValue,
                color: cond.result,
                fieldType,
            };
        }));
    }, [state.conditionalFormats, selectedFields]);
    // Emit one rule per visual row so the on-screen order stays stable across
    // edits. Grouping by column name (as we used to) caused rows to shuffle
    // whenever an edit made two rows share the same column, because the shared
    // rule "pulls" both rows next to each other. Downstream evaluation merges
    // rules that share a column, so this split is safe (see
    // `buildStyleFromConditionalFormats`).
    const rebuildConditionalFormats = useCallback((rows) => {
        if (rows.length === 0) {
            return undefined;
        }
        return rows.map((row) => {
            const config = getOperatorConfig(row.fieldType, row.operator);
            const requiresValue = operatorRequiresValue(row.operator);
            const persistedValue = requiresValue ? row.value : '';
            const persistedEndValue = config?.valueKind === 'range' ? row.endValue : undefined;
            const condition = {
                operator: row.operator,
                value: persistedValue,
                endValue: persistedEndValue,
                result: row.color,
                fieldType: row.fieldType,
            };
            return {
                columnName: row.columnName,
                property: 'backgroundColor',
                conditions: [condition],
            };
        });
    }, []);
    const applyRows = useCallback((rows) => {
        onChange({ ...state, conditionalFormats: rebuildConditionalFormats(rows) });
    }, [state, onChange, rebuildConditionalFormats]);
    const handleColumnChange = useCallback((index, columnKey) => {
        const nextFieldType = resolveFieldType(columnKey, selectedFields);
        const updated = conditions.map((row, i) => {
            if (i !== index) {
                return row;
            }
            const currentOpConfig = getOperatorConfig(row.fieldType, row.operator);
            const stillValid = !!getOperatorConfig(nextFieldType, row.operator);
            return {
                ...row,
                columnName: columnKey,
                fieldType: nextFieldType,
                operator: stillValid ? row.operator : pickDefaultOperator(nextFieldType),
                // Clear stored values when switching between value kinds (e.g. text→date)
                // or when the new operator doesn't match the previous one's shape.
                value: stillValid && currentOpConfig?.valueKind !== 'range' ? row.value : '',
                endValue: undefined,
            };
        });
        applyRows(updated);
    }, [conditions, selectedFields, applyRows]);
    const handleOperatorChange = useCallback((index, operator) => {
        const updated = conditions.map((row, i) => {
            if (i !== index) {
                return row;
            }
            const nextConfig = getOperatorConfig(row.fieldType, operator);
            const nextValueKind = nextConfig?.valueKind ?? 'single';
            return {
                ...row,
                operator,
                value: nextValueKind === 'none' ? '' : row.value,
                endValue: nextValueKind === 'range' ? row.endValue : undefined,
            };
        });
        applyRows(updated);
    }, [conditions, applyRows]);
    const handleValueChange = useCallback((index, value) => {
        const updated = conditions.map((row, i) => (i === index ? { ...row, value } : row));
        applyRows(updated);
    }, [conditions, applyRows]);
    const handleEndValueChange = useCallback((index, endValue) => {
        const updated = conditions.map((row, i) => (i === index ? { ...row, endValue } : row));
        applyRows(updated);
    }, [conditions, applyRows]);
    const handleColorChange = useCallback((index, color) => {
        const updated = conditions.map((row, i) => (i === index ? { ...row, color } : row));
        applyRows(updated);
    }, [conditions, applyRows]);
    const handleRemoveCondition = useCallback((index) => {
        const updated = conditions.filter((_, i) => i !== index);
        applyRows(updated);
    }, [conditions, applyRows]);
    // New rows start with an empty column so the user has to explicitly pick one
    // — matching the "+ Add field" flow in the dashboard Edit filters modal.
    // Pre-filling the first available field made every new row visually mirror
    // whatever existing row also used that column, which reads as "the first
    // condition got duplicated".
    const handleAddCondition = useCallback(() => {
        const defaultColor = PALETTE_COLORS[currentPalette][0]?.value || 'red';
        const emptyRow = {
            columnName: '',
            operator: pickDefaultOperator('string'),
            value: '',
            endValue: undefined,
            color: defaultColor,
            fieldType: undefined,
        };
        applyRows([...conditions, emptyRow]);
    }, [applyRows, conditions, currentPalette]);
    return (_jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx(Select, { label: translate('Palette'), options: paletteOptions, value: paletteOptions.find((o) => o.value === currentPalette), onChange: handlePaletteChange, width: 'full', disabled: !isEditable }), !hideConditionalFormatting && (_jsx(Card, { className: 'nucleus-bg-neutral-50 nucleus-border nucleus-border-neutral-200 nucleus-shadow-none', children: _jsxs(Card.Content, { className: 'p-4', children: [_jsx(Text, { size: 'sm', className: 'font-semibold text-neutral-900 mb-4', children: translate('Conditional formatting') }), _jsx("div", { className: 'flex flex-col', children: conditions.map((cond, index) => {
                                const condKey = `condition-${cond.columnName}-${cond.operator}-${index}`;
                                const operatorConfig = getOperatorConfig(cond.fieldType, cond.operator);
                                const operatorOptions = getOperatorsForFieldType(cond.fieldType).map((op) => ({
                                    label: translate(op.labelKey),
                                    value: op.value,
                                }));
                                const selectedOperatorOption = operatorOptions.find((o) => o.value === cond.operator) ?? operatorOptions[0];
                                const valueKind = operatorConfig?.valueKind ?? 'single';
                                const isTimeField = cond.fieldType === 'time';
                                const isNumberField = cond.fieldType === 'number';
                                return (_jsxs("div", { children: [index > 0 && _jsx("hr", { className: 'border-neutral-200 my-4' }), _jsxs("div", { className: 'flex flex-col gap-4', children: [_jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'text-neutral-500', children: translate('If') }), _jsxs("div", { className: 'flex items-center gap-2 min-w-0', children: [_jsx("div", { className: 'flex-1 min-w-0', children: _jsx(Select, { dataTestId: `chart-builder-conditional-format-column-${index}`, options: columnOptions, value: columnOptions.find((o) => o.value === cond.columnName), onChange: (opt) => handleColumnChange(index, opt.value), width: 'full', disabled: !isEditable, searchableOptions: true }) }), isEditable && (_jsx(Button, { label: translate('Remove condition'), content: _jsx(Icon, { name: 'delete', size: 'sm' }), variant: 'transparent', onClick: () => handleRemoveCondition(index) }))] })] }), _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'text-neutral-500', children: translate('is') }), _jsxs("div", { className: 'grid grid-cols-2 gap-2', children: [_jsx(Select, { dataTestId: `chart-builder-conditional-format-operator-${index}`, options: operatorOptions, value: selectedOperatorOption, onChange: (opt) => handleOperatorChange(index, opt.value), width: 'full', disabled: !isEditable }), valueKind === 'single' &&
                                                                    (isTimeField ? (_jsx(DatePicker, { id: `condition-value-${index}`, value: toPlainDateOrUndefined(cond.value), onChange: (date) => handleValueChange(index, date ? date.toString() : ''), width: 'full' })) : (_jsx(Input, { id: `condition-value-${index}`, type: isNumberField ? 'number' : 'text', value: String(cond.value ?? ''), onChange: (e) => handleValueChange(index, e.target.value), disabled: !isEditable }))), valueKind === 'range' && (_jsxs("div", { className: 'grid grid-cols-2 gap-2', children: [_jsx(DatePicker, { id: `condition-value-from-${index}`, value: toPlainDateOrUndefined(cond.value), onChange: (date) => handleValueChange(index, date ? date.toString() : ''), width: 'full' }), _jsx(DatePicker, { id: `condition-value-to-${index}`, value: toPlainDateOrUndefined(cond.endValue), onChange: (date) => handleEndValueChange(index, date ? date.toString() : undefined), width: 'full' })] }))] })] }), _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'text-neutral-500', children: translate('then color it') }), _jsx(Select, { options: colorOptions, value: colorOptions.find((o) => o.value === cond.color), onChange: (opt) => handleColorChange(index, opt.value), width: 'full', disabled: !isEditable })] })] })] }, condKey));
                            }) }), isEditable && (_jsx("div", { className: `self-start ${conditions.length > 0 ? 'mt-4' : ''}`, children: _jsx(Button, { label: `+ ${translate('Add condition')}`, variant: 'transparent', onClick: handleAddCondition }) }))] }) }))] }));
};
